import React, { useCallback, useEffect, useState } from 'react';
import { Button, Input, Spinner } from '../../components';
import { branchService } from '../../services/branchService';
import type { Branch } from '../../types/branch';
import type { ApiError } from '../../types/api';
import './CreateBranchModal.scss';

export interface CreateBranchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (branch: Branch) => void;
}

export const CreateBranchModal: React.FC<CreateBranchModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const reset = useCallback(() => {
    setName('');
    setError(null);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      reset();
      return;
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) onClose();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isSaving, onClose, reset]);

  const handleSubmit = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Branch name is required.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const branch = await branchService.createBranch({ name: trimmedName });
      onSuccess(branch);
      onClose();
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Failed to create branch.');
    } finally {
      setIsSaving(false);
    }
  }, [name, onClose, onSuccess]);

  if (!isOpen) return null;

  return (
    <div
      className="create-branch-modal-overlay"
      onClick={isSaving ? undefined : onClose}
      aria-hidden="true"
    >
      <div
        className="create-branch-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-branch-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="create-branch-modal__header">
          <h3 id="create-branch-modal-title" className="create-branch-modal__title">
            Create Branch
          </h3>
          <button
            type="button"
            className="create-branch-modal__close"
            onClick={onClose}
            disabled={isSaving}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="create-branch-modal__body">
          {error && <div className="create-branch-modal__error">{error}</div>}

          <Input
            label="Branch Name"
            value={name}
            onChange={(e) => {
              setError(null);
              setName(e.target.value);
            }}
            placeholder="e.g., London Office"
            maxLength={100}
            required
            disabled={isSaving}
          />
        </div>

        <footer className="create-branch-modal__footer">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isSaving}
            className="create-branch-modal__cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSaving || !name.trim()}
            className="create-branch-modal__submit"
          >
            {isSaving ? <Spinner size="small" /> : null}
            {isSaving ? 'Creating...' : 'Create Branch'}
          </Button>
        </footer>
      </div>
    </div>
  );
};
